import { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import CaparraPayButton from "./CaparraPayButton";
import SignaturePad from "./SignaturePad";
import { Loader2, FileSignature, CheckCircle2, ShoppingCart, Lock, ShieldAlert, PenLine, XCircle } from "lucide-react";

export default function OrderFlow({ lead, caparra, contract, vessatorie = [], onAccepted, publicToken = null, noCaparra = false }) {
  const acc = contract?.accettazione;
  const paid = !!contract?.caparra_pagata;
  const accepted = !!acc?.accettato;
  const authCtx = useAuth() || {};
  const isAdmin = ["admin", "team_leader"].includes(authCtx.user?.role);
  const canCancel = !publicToken && isAdmin && accepted && !paid;
  const [cancelling, setCancelling] = useState(false);
  const acceptUrl = publicToken ? `/public/outlook/${publicToken}/accept-contract` : `/leads/${lead.id}/accept-contract`;
  const contractToken = publicToken || lead?.outlook_token;
  const contractPdfHref = contractToken
    ? `/p/${contractToken}/contratto`
    : `/leads/${lead.id}/contratto`;
  const [nome, setNome] = useState([lead.nome, lead.cognome].filter(Boolean).join(" ") || lead.name || "");
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [vessChecked, setVessChecked] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  const openDialog = () => {
    if (!nome.trim()) { toast.error("Inserisci nome e cognome"); return; }
    if (!checked) { toast.error("Spunta la casella di accettazione"); return; }
    setVessChecked(false);
    setSigEmpty(true);
    setOpen(true);
  };

  const confirm = async () => {
    if (!vessChecked) { toast.error("Approva specificamente le clausole vessatorie"); return; }
    const firma = sigRef.current?.toDataURL();
    if (!firma) { toast.error("Apponi la firma autografa nel riquadro"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(acceptUrl, {
        nome: nome.trim(),
        firma_img: firma,
        clausole_vessatorie: vessatorie.map((v) => v.titolo),
      });
      onAccepted?.(data.accettazione);
      setOpen(false);
      toast.success("Contratto firmato e accettato");
      // Apre il contratto firmato in PDF (design proposta commerciale + firma digitale),
      // generato e archiviato lato server (Playwright) così è allegato alle email di conferma.
      window.open(contractPdfHref, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async () => {
    if (!window.confirm("Annullare l'ordine? La firma del contratto verrà resettata e il cliente dovrà firmare di nuovo. Operazione possibile solo perché la caparra non è stata pagata.")) return;
    setCancelling(true);
    try {
      await api.post(`/leads/${lead.id}/cancel-order`);
      onAccepted?.(null);
      toast.success("Ordine annullato: firma resettata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="rounded-xl border-2 border-primary/40 bg-primary/5 p-6 no-print" data-testid="order-flow">
      <h2 className="text-sm uppercase tracking-[0.15em] text-primary font-bold mb-1 flex items-center gap-2">
        <ShoppingCart className="h-4 w-4" strokeWidth={1.5} /> Procedi con l'ordine
      </h2>
      <p className="text-xs text-muted-foreground mb-5">{noCaparra ? "Accetta e firma la proposta: con la firma l'ordine è confermato." : "Due semplici passaggi per confermare l'ordine: accetta e firma la proposta, poi versa la caparra. La caparra è a titolo di conferma e verrà interamente scomputata dal saldo finale."}</p>

      {/* Step 1 — Firma / accettazione */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${accepted ? "bg-green-500 text-black" : "bg-primary text-black"}`}>1</span>
          <span className="font-semibold text-sm">Firma il contratto</span>
        </div>
        {accepted ? (
          <div className="flex items-start gap-2 text-sm text-green-500 font-medium" data-testid="contract-accepted">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div>Contratto firmato da <b>{acc.nome}</b> il {new Date(acc.data).toLocaleString("it-IT")}</div>
              <div className="text-xs text-muted-foreground font-normal mt-0.5">
                {acc.ip ? `IP ${acc.ip} · ` : ""}{(acc.clausole_vessatorie?.length || 0)} clausole vessatorie approvate (art. 1341 c.c.)
              </div>
              {acc.firma_img && (
                <img src={acc.firma_img} alt="Firma" className="mt-2 h-16 rounded border border-border bg-white p-1" data-testid="accepted-signature" />
              )}
              <Button asChild variant="outline" size="sm" className="mt-3 border-primary/50 text-primary hover:bg-primary/10" data-testid="download-signed-contract-btn">
                <a href={contractPdfHref} target="_blank" rel="noopener noreferrer">
                  <FileSignature className="h-4 w-4 mr-1.5" /> Scarica contratto firmato (PDF)
                </a>
              </Button>
              {canCancel && (
                <Button variant="outline" size="sm" onClick={cancelOrder} disabled={cancelling} className="mt-3 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="cancel-order-btn">
                  {cancelling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />} Annulla ordine
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nome e cognome del firmatario</label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome Cognome" data-testid="accept-nome" className="mt-1" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground leading-relaxed">
              <Checkbox checked={checked} onCheckedChange={setChecked} data-testid="accept-checkbox" className="mt-0.5" />
              <span>Ho letto e accetto la proposta commerciale e le <b className="text-foreground">Condizioni Generali di Vendita</b>. Confermo la volontà di procedere con l'ordine dell'impianto fotovoltaico chiavi in mano.</span>
            </label>
            <Button onClick={openDialog} className="font-semibold" data-testid="accept-contract-btn">
              <FileSignature className="h-4 w-4 mr-2" /> Firma / Accetta il contratto
            </Button>
          </div>
        )}
      </div>

      {/* Step 2 — Caparra */}
      {!noCaparra && (
      <div className="rounded-lg border border-border bg-card p-4 mt-3">
        <div className="flex items-center gap-2 mb-3">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${paid ? "bg-green-500 text-black" : accepted ? "bg-primary text-black" : "bg-secondary text-muted-foreground"}`}>2</span>
          <span className={`font-semibold text-sm ${accepted ? "" : "text-muted-foreground"}`}>Versa la caparra</span>
        </div>
        {!accepted ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="caparra-locked">
            <Lock className="h-3.5 w-3.5" /> Disponibile dopo la firma del contratto.
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">Pagamento sicuro con carta, Apple Pay o Google Pay tramite Stripe.</p>
            <CaparraPayButton leadId={lead.id} publicToken={publicToken} caparra={caparra} paid={paid} commessa={contract?.commessa} testid="order-pay-caparra-btn" />
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border/60 leading-snug" data-testid="caparra-terms">
          La caparra è versata <b>esclusivamente a titolo di conferma dell'ordine</b> e verrà <b>interamente scomputata dal saldo finale</b>: non è un costo aggiuntivo, ma un acconto che riduce di pari importo la cifra da saldare alla consegna.
        </p>
      </div>
      )}

      {/* Dialog clausole vessatorie + firma autografa */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="vessatorie-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /> Approvazione specifica delle clausole (art. 1341 c.c.)</DialogTitle>
            <DialogDescription>
              Ai sensi degli artt. 1341 e 1342 c.c. le seguenti clausole richiedono una specifica approvazione per iscritto. Leggile e apponi la tua firma autografa per confermarle.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-52 overflow-y-auto rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid="vessatorie-list">
            {vessatorie.length ? vessatorie.map((v, i) => (
              <div key={i} data-testid={`vessatoria-${i}`}>
                <div className="text-sm font-semibold text-foreground">{v.titolo}</div>
                <p className="text-xs text-muted-foreground leading-relaxed">{v.testo}</p>
              </div>
            )) : <p className="text-xs text-muted-foreground">Nessuna clausola vessatoria specifica.</p>}
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground leading-relaxed">
            <Checkbox checked={vessChecked} onCheckedChange={setVessChecked} data-testid="vessatorie-checkbox" className="mt-0.5" />
            <span>Dichiaro di approvare specificamente, ai sensi degli artt. 1341 e 1342 c.c., le clausole sopra elencate.</span>
          </label>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1.5"><PenLine className="h-3.5 w-3.5" /> Firma autografa</label>
            <SignaturePad ref={sigRef} onChange={setSigEmpty} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="vessatorie-cancel">Annulla</Button>
            <Button onClick={confirm} disabled={saving || !vessChecked || sigEmpty} className="font-semibold" data-testid="vessatorie-confirm">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSignature className="h-4 w-4 mr-2" />} Conferma e firma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
